// =====================================================
// STORY REDLINE: redlining one paired paragraph of a footnote, endnote, header or footer, and a
// sequence of them. A header or footer line with fields (a page number, a document id) is marked
// word by word around them.
// =====================================================

import { W_NS, tagCrossRelIds, parseEl, serializeClean } from './xml-helpers';
import { buildRun, buildRevisionRun, diffRunsXml, applyRevisionToAllRuns } from './docx-tracking';
import { computeWordDiff, diffArrays, wordsEqual, refineWordReplacements, withWordIndices, wordOf } from './diff-engine-core';
import { addDeleteMarkup, addInsertMarkup } from './docx-markup';
import { parseParagraphXml } from './parsers-core';
import { NOTE_TEXT_NORM, fragmentText, withoutPageNumbers, PAGE_FIELD, PPR_RE, STRUCTURAL_PARA } from './story-text';

// ---- header and footer paragraphs that hold fields (a page number, a document id) ----

/** A block of a header/footer paragraph kept as it is: a field from its begin to its end, a
 *  w:fldSimple, or a run holding only a positional tab, break or symbol. `key` says what it is
 *  (two blocks with the same key are the same; a page number's shown number is not part of it). */
export type HfBlock = { xml: string; key: string; simple: boolean };
/** A header/footer paragraph as one string, each block in it as BLOCK_CHAR, with the
 *  formatting of each stretch of text. */
export type HfLine = { pPr: string; text: string; runs: { start: number; rPr: string }[]; blocks: HfBlock[] };
export const BLOCK_CHAR = '\uE000';

export const W_EL = (n: Node): n is Element => n.nodeType === 1 && (n as Element).namespaceURI === W_NS;
export const kidsOf = (e: Element): Element[] => Array.from(e.childNodes).filter(W_EL);
export const wAttr = (e: Element, name: string) => e.getAttributeNS(W_NS, name) || e.getAttribute('w:' + name) || '';
/** What a field is, for pairing: its instruction without the formatting switch; a page number
 *  by its name alone ("PAGE \* MERGEFORMAT" and "PAGE" are the same field), anything else
 *  with what it shows (a document id that changed is a change). */
export function fieldKey(instr: string, shown: string): string {
  const i = instr.replace(/\\\*\s*MERGEFORMAT/gi, '').replace(/\s+/g, ' ').trim();
  return PAGE_FIELD.test(i) ? 'field:' + i.split(' ')[0].toUpperCase() : 'field:' + i.toUpperCase() + '\u0002' + shown;
}

/** A header/footer paragraph as an HfLine, or null when it holds anything else (a revision, a
 *  hyperlink, a content control, a drawing …) or a field that does not close in it. */
export function hfLine(pXml: string): HfLine | null {
  // (a tracked change to the paragraph's properties is kept with them, as the other path does)
  if (/<w:(?:ins|del|moveFrom|moveTo|rPrChange|hyperlink|sdt|drawing|pict|object|txbxContent|tbl)\b/.test(pXml.replace(/<w:pPrChange\b[\s\S]*?<\/w:pPrChange>/g, ''))) return null;
  let p: Element;
  try { p = parseEl(pXml); } catch { return null; }
  const line: HfLine = { pPr: '', text: '', runs: [], blocks: [] };
  const addBlock = (b: HfBlock) => { line.runs.push({ start: line.text.length, rPr: '' }); line.text += BLOCK_CHAR; line.blocks.push(b); };
  let field: { xml: string; instr: string; shown: string; depth: number; inResult: boolean } | null = null;
  for (const e of kidsOf(p)) {
    const ln = e.localName;
    if (ln === 'pPr') { line.pPr = serializeClean(e); continue; }
    if (ln === 'bookmarkStart' || ln === 'bookmarkEnd' || ln === 'proofErr' || ln === 'permStart' || ln === 'permEnd') {
      if (field) field.xml += serializeClean(e);
      continue;
    }
    if (ln === 'fldSimple') {
      if (field) return null;
      const shown = Array.from(e.getElementsByTagNameNS(W_NS, 't')).map(t => t.textContent || '').join('');
      addBlock({ xml: serializeClean(e), key: fieldKey(wAttr(e, 'instr'), shown), simple: true });
      continue;
    }
    if (ln !== 'r') return null;
    const kids = kidsOf(e).filter(k => k.localName !== 'rPr' && k.localName !== 'lastRenderedPageBreak');
    if (field || kids.some(k => k.localName === 'fldChar' || k.localName === 'instrText')) {
      // a field's runs, from its begin to its end (one run may hold all of it)
      if (!field) {
        if (kids[0]?.localName !== 'fldChar' || wAttr(kids[0], 'fldCharType') !== 'begin') return null;
        field = { xml: '', instr: '', shown: '', depth: 0, inResult: false };
      }
      field.xml += serializeClean(e);
      for (const k of kids) {
        if (!field) return null;   // more after the field ended inside this run
        if (k.localName === 'fldChar') {
          const t = wAttr(k, 'fldCharType');
          if (t === 'begin') field.depth++;
          else if (t === 'separate') { if (field.depth === 1) field.inResult = true; }
          else if (t === 'end' && --field.depth === 0) {
            addBlock({ xml: field.xml, key: fieldKey(field.instr, field.shown), simple: false });
            field = null;
          }
        } else if (k.localName === 'instrText') { if (field.depth === 1 && !field.inResult) field.instr += k.textContent || ''; }
        else if (k.localName === 't') { if (field.depth === 1 && field.inResult) field.shown += k.textContent || ''; }
      }
      continue;
    }
    if (!kids.length) continue;
    if (kids.every(k => k.localName === 'ptab' || k.localName === 'br' || k.localName === 'cr' || k.localName === 'sym')) {
      addBlock({ xml: serializeClean(e), key: 'run:' + kids.map(k => serializeClean(k)).join('').replace(/\s+w:rsid\w*="[^"]*"/g, ''), simple: false });
      continue;
    }
    let text = '';
    for (const k of kids) {
      if (k.localName === 't') text += k.textContent || '';
      else if (k.localName === 'tab') text += '\t';
      else if (k.localName === 'noBreakHyphen') text += '\u2011';
      else if (k.localName === 'softHyphen') continue;
      else return null;   // a footnote mark, a ruby, …
    }
    if (text) {
      const rPr = kidsOf(e).find(k => k.localName === 'rPr');
      line.runs.push({ start: line.text.length, rPr: rPr ? serializeClean(rPr) : '' });
      line.text += text;
    }
  }
  return field ? null : line;
}

/** A run (or its revision) for header/footer text, with each tab written as <w:tab/>. */
export function hfRun(text: string, rPr: string, type: 'equal' | 'insert' | 'delete'): string {
  const xml = type === 'equal' ? buildRun(text, rPr) : buildRevisionRun(text, rPr, type);
  return xml.replace(/<w:(t|delText)( xml:space="preserve")?>([^<]*)<\/w:\1>/g, (_m, tag: string, _sp: string, body: string) =>
    body.split('\t').map(b => `<w:${tag} xml:space="preserve">${b}</w:${tag}>`).join('<w:tab/>').replace(new RegExp(`<w:${tag} xml:space="preserve"></w:${tag}>`, 'g'), ''));
}

/** A block as inserted or deleted (its runs wrapped), or null if it cannot be (a w:fldSimple
 *  cannot sit inside w:ins or w:del). */
export function hfBlockRevision(b: HfBlock, type: 'insert' | 'delete'): string | null {
  if (b.simple) return null;
  const p = applyRevisionToAllRuns(`<w:p>${b.xml}</w:p>`, type === 'insert' ? addInsertMarkup : addDeleteMarkup, type);
  const m = p.match(new RegExp(`^<w:p\\b[^>]*>(?:${PPR_RE.source})?([\\s\\S]*)<\\/w:p>$`));
  return m ? m[1] : null;
}

/**
 * Redline a changed header/footer paragraph that holds fields (or positional tabs, breaks,
 * symbols) word by word, each block counting as one word: unchanged words keep the revised
 * draft's formatting and spacing (tabs too), deleted ones the original's; unchanged blocks are
 * kept as the revised draft has them (a page number shows the revised draft's number), added
 * or removed fields are marked. Null when the paragraph holds something else, or a block that
 * cannot be marked changed.
 */
export function redlineFieldParagraph(rp: string, op: string): string | null {
  const r = hfLine(rp), o = hfLine(op);
  if (!r || !o) return null;
  const tokens = (l: HfLine) => {
    const out: { key: string; start: number; end: number }[] = [];
    let b = 0;
    for (const m of l.text.matchAll(new RegExp(`${BLOCK_CHAR}|[^\\s${BLOCK_CHAR}]+`, 'g'))) {
      out.push({ key: m[0] === BLOCK_CHAR ? '\u0001' + l.blocks[b++].key : m[0], start: m.index!, end: m.index! + m[0].length });
    }
    return out;
  };
  const ot = tokens(o), rt = tokens(r);
  const eq = (x: string, y: string) => x === y || (x[0] !== '\u0001' && y[0] !== '\u0001' && wordsEqual(x, y));
  const stretches: { type: 'equal' | 'insert' | 'delete'; from: number; to: number }[] = [];
  const add = (type: 'equal' | 'insert' | 'delete', from: number, to: number) => {
    const last = stretches[stretches.length - 1];
    if (last && last.type === type && last.to === from) last.to = to;
    else stretches.push({ type, from, to });
  };
  // The word diff with the same refinement as the body (an edit inside a small one-word swap,
  // punctuation kept beside a changed word: "January [-5-]{+19+}, 2026"; a block is one atomic
  // word). Each piece is placed by its own word and offset; a piece that starts its word also
  // takes the space before it, from the end of the word before on its side.
  const ds = withWordIndices(diffArrays(ot.map(x => x.key), rt.map(x => x.key), eq));
  const range = (toks: { start: number; end: number }[], w: number, off: number | undefined, len: number, glued: boolean | undefined) => {
    const at = toks[w].start + (off ?? 0);
    return { from: glued ? at : (w > 0 ? toks[w - 1].end : 0), to: off == null ? toks[w].end : at + len };
  };
  let rEnd = 0;
  for (const d of refineWordReplacements(ds)) {
    if (d.type === 'delete') { const x = range(ot, wordOf(d, 'orig'), d.origCharIdx, d.value.length, d.glued); add('delete', x.from, x.to); }
    else { const x = range(rt, wordOf(d, 'rev'), d.revCharIdx, d.value.length, d.glued); add(d.type === 'equal' ? 'equal' : 'insert', x.from, x.to); rEnd = x.to; }
  }
  if (rEnd < r.text.length) add('equal', rEnd, r.text.length);
  // the blocks before a position of a line: which block a BLOCK_CHAR is
  const blockAt = (l: HfLine, at: number) => { let n = 0; for (let i = 0; i < at; i++) if (l.text[i] === BLOCK_CHAR) n++; return l.blocks[n]; };
  const rPrAt = (l: HfLine, at: number) => { let rPr = ''; for (const x of l.runs) { if (x.start <= at) rPr = x.rPr; else break; } return rPr; };
  let runs = '';
  for (const x of stretches) {
    const l = x.type === 'delete' ? o : r;
    for (let i = x.from; i < x.to;) {
      if (l.text[i] === BLOCK_CHAR) {
        const b = blockAt(l, i);
        const xml = x.type === 'equal' ? b.xml : hfBlockRevision(b, x.type);
        if (xml === null) return null;
        runs += xml;
        i++;
        continue;
      }
      const rPr = rPrAt(l, i);
      let j = i + 1;
      while (j < x.to && l.text[j] !== BLOCK_CHAR && rPrAt(l, j) === rPr) j++;
      runs += hfRun(l.text.slice(i, j), rPr, x.type);
      i = j;
    }
  }
  return `<w:p>${r.pPr}${runs}</w:p>`;
}

/**
 * Redline an index-paired sequence of paragraphs (rev vs orig): unchanged
 * paragraphs pass through verbatim, changed ones are rebuilt with a word-level
 * diff (rev pPr and an optional lead run — the footnoteRef — preserved),
 * one-sided paragraphs become whole insertions/deletions. Returns null when a
 * CHANGED paragraph is structural (fields/tables/drawings) — the caller keeps
 * the fragment verbatim rather than risk destroying it.
 */
export function redlineParaSequence(rParas: string[], oParas: string[], leadRunRe: RegExp | null): string | null {
  let inner = '';
  const n = Math.max(rParas.length, oParas.length);
  for (let i = 0; i < n; i++) {
    const rp = rParas[i], op = oParas[i];
    if (rp && op) {
      const one = redlineOnePara(rp, op, leadRunRe);
      if (one === null) return null;
      inner += one;
    } else if (rp) {
      if (STRUCTURAL_PARA.test(rp)) return null;
      inner += applyRevisionToAllRuns(rp, addInsertMarkup, 'insert');
    } else if (op) {
      if (STRUCTURAL_PARA.test(op)) return null;
      inner += applyRevisionToAllRuns(tagCrossRelIds(op), addDeleteMarkup, 'delete');
    }
  }
  return inner;
}

/** One paired paragraph of a note, header or footer: as it is when unchanged, else redlined;
 *  null when it cannot be redlined safely. */
export function redlineOnePara(rp: string, op: string, leadRunRe: RegExp | null): string | null {
  // (a page number Word last showed differently is no change: withoutPageNumbers)
  if (NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(rp))) === NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(op)))) return rp;
  // a header or footer line: word by word around its fields and tabs, keeping each run's formatting
  if (!leadRunRe) {
    const fp = redlineFieldParagraph(rp, op);
    if (fp !== null) return fp;
  }
  if (STRUCTURAL_PARA.test(rp) || STRUCTURAL_PARA.test(op)) return null;
  const pPr = (rp.match(PPR_RE) || [''])[0];
  // the same writer as body paragraphs: each word's own formatting (an italic case name stays
  // italic), the source's spacing, one revision per run of changed words, and what is not a word
  // where it was: the note's own number mark first (the parser records it, like a link or a field)
  const r = parseParagraphXml(rp), o = parseParagraphXml(op);
  const runs = diffRunsXml(computeWordDiff(o.text, r.text), o, r);
  const leadRun = leadRunRe && !r.inline ? ((rp.match(leadRunRe) || [''])[0]) : '';
  return `<w:p>${pPr}${leadRun}${runs}</w:p>`;
}
